import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  useEffect,
  type ReactNode,
} from "react";

import {
  favoritesControllerGetFavorites,
  favoritesControllerAddFavorite,
  favoritesControllerRemoveFavorite,
} from "@/api/generated/endpoints";

interface FavoritesContextValue {
  favorites: string[];
  isFavorite: (id: string) => boolean;
  toggleFavorite: (id: string) => void;
}

const FavoritesContext = createContext<FavoritesContextValue | null>(null);

interface FavoritesProviderProps {
  readonly children: ReactNode;
}

export function FavoritesProvider({ children }: FavoritesProviderProps) {
  const [favorites, setFavorites] = useState<string[]>([]);

  useEffect(() => {
    favoritesControllerGetFavorites()
      .then((res) => setFavorites(res.data.favorites))
      .catch(() => {
        // Not authenticated or API unavailable — start with empty list
      });
  }, []);

  const toggleFavorite = useCallback(
    (id: string) => {
      const isCurrentlyFavorite = favorites.includes(id);

      // Optimistic update so the UI responds immediately
      setFavorites((prev) =>
        isCurrentlyFavorite ? prev.filter((fav) => fav !== id) : [...prev, id],
      );

      const apiCall = isCurrentlyFavorite
        ? favoritesControllerRemoveFavorite(id)
        : favoritesControllerAddFavorite(id);

      apiCall.catch(() => {
        // Revert optimistic update on failure. Use prev (current state at
        // revert time) to avoid stale-closure races from rapid toggles:
        // deduplicate when re-adding so a concurrent successful add doesn't
        // produce duplicate entries if a prior remove fails late.
        setFavorites((prev) =>
          isCurrentlyFavorite
            ? prev.includes(id)
              ? prev
              : [...prev, id]
            : prev.filter((fav) => fav !== id),
        );
      });
    },
    [favorites],
  );

  const isFavorite = useCallback(
    (id: string) => favorites.includes(id),
    [favorites],
  );

  const value = useMemo(
    () => ({ favorites, isFavorite, toggleFavorite }),
    [favorites, isFavorite, toggleFavorite],
  );

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error("useFavorites must be used within a FavoritesProvider");
  }
  return context;
}
